/**
 * IndexedDB persister for the TanStack Query cache.
 *
 * Storage: `idb-keyval` (tiny, promise-based IndexedDB wrapper) with a
 * dedicated database/store so the query cache is PARTITIONED away from
 * any other IndexedDB usage (LiveKit, service worker, etc.).
 *
 * Bloat control — three independent mechanisms:
 *   1. OPT-IN persistence: only queries declaring `meta: { persist: true }`
 *      are written to disk (see shouldDehydrateQuery below). Everything
 *      else stays memory-only. This keeps the stored payload tiny and
 *      makes offline support an explicit, reviewable decision per query.
 *   2. maxAge (24 h default): expired snapshots are discarded on restore.
 *   3. buster: bump REACT_APP_QUERY_CACHE_BUSTER (or app version) and
 *      every old snapshot is invalidated at boot — plus
 *      `clearPersistedCache()` for programmatic wipes (e.g. on logout).
 */
import { get, set, del, createStore } from 'idb-keyval';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import {
  IDB_DATABASE, IDB_STORE, IDB_KEY, PERSIST_BUSTER, PERSIST_MAX_AGE_MS,
} from './config';

// Dedicated partition: mesp-query-cache / tanstack.
const idbStore = createStore(IDB_DATABASE, IDB_STORE);

// idb-keyval → AsyncStorage adapter (get/set/removeItem contract).
const indexedDBStorage = {
  getItem: async (key) => {
    const value = await get(key, idbStore);
    return value ?? null;
  },
  setItem: (key, value) => set(key, value, idbStore),
  removeItem: (key) => del(key, idbStore),
};

export const queryPersister = createAsyncStoragePersister({
  storage: indexedDBStorage,
  key: IDB_KEY,
  // Debounce writes: at most one IndexedDB write per second even during
  // rapid cache churn (navigation bursts) — protects low-end devices.
  throttleTime: 1000,
});

/** Options consumed by <PersistQueryClientProvider persistOptions={...}> */
export const persistOptions = {
  persister: queryPersister,
  maxAge: PERSIST_MAX_AGE_MS,
  buster: PERSIST_BUSTER,
  dehydrateOptions: {
    // Partition rule #1: opt-in only. A query is persisted to
    // IndexedDB IFF it was declared with `meta: { persist: true }`
    // AND it holds successful data (never persist error states).
    shouldDehydrateQuery: (query) =>
      query.state.status === 'success' && query.meta?.persist === true,
  },
};

/** Wipe the persisted snapshot (call on logout / account switch). */
export async function clearPersistedCache() {
  try {
    await del(IDB_KEY, idbStore);
  } catch (_e) { /* IndexedDB unavailable (private mode) — non-fatal */ }
}
